/**
 * Round scoring: contract success / bait (бейт), contra multipliers, kapot.
 *
 * Card-point sums should match {@link ./tricks} (`trickCardPointsByTeam` excludes the +10 last-trick bonus).
 *
 * @module game/belot/armenian/scoring
 */

const { KAPOT_CONTRACT_POINTS } = require("./auction");

/**
 * @typedef {1|2|4} BelotContraMultiplier
 */

/**
 * @typedef {{
 *   contractTeam: 0|1,
 *   contractTargetPoints: number,
 *   contraMultiplier?: BelotContraMultiplier,
 *   kapotBid?: boolean,
 *   noTrumpContract?: boolean,
 *   tricksWonByTeam: [number, number],
 *   trickCardPointsByTeam: [number, number],
 *   lastTrickWinnerTeam: 0|1,
 *   declarationPointsByTeam: [number, number],
 *   losingContractTeamDeclarationPointsForBait?: number,
 *   shulerBonusByTeam?: [number, number],
 * }} BelotRoundScoreInput
 */

/**
 * @typedef {{
 *   delta: [number, number],
 *   success: boolean,
 *   bait: boolean,
 *   contractTeamTrickPlusDecl: number,
 * }} BelotRoundScoreResult
 */

/**
 * @param {unknown} v
 * @returns {v is 0|1}
 */
function isTeam(v) {
  return v === 0 || v === 1;
}

/**
 * @param {BelotRoundScoreInput} input
 * @returns {BelotRoundScoreResult}
 */
function scoreRound(input) {
  if (!input || typeof input !== "object") {
    throw new TypeError("scoring.scoreRound: input required");
  }
  const ct = input.contractTeam;
  if (!isTeam(ct)) throw new Error("scoring: contractTeam must be 0 or 1");
  const ot = /** @type {0|1} */ (1 - ct);
  const mult = input.contraMultiplier ?? 1;
  if (mult !== 1 && mult !== 2 && mult !== 4) {
    throw new Error("scoring: contraMultiplier must be 1, 2, or 4");
  }
  const tricks = input.tricksWonByTeam;
  if (!Array.isArray(tricks) || tricks.length !== 2) {
    throw new Error("scoring: tricksWonByTeam must be [t0,t1]");
  }
  const tSum = tricks[0] + tricks[1];
  if (tSum !== 8) {
    throw new Error("scoring: tricks must sum to 8 (32-card deal)");
  }
  const tcp = input.trickCardPointsByTeam;
  if (!Array.isArray(tcp) || tcp.length !== 2) {
    throw new Error("scoring: trickCardPointsByTeam must be [p0,p1]");
  }
  const last = input.lastTrickWinnerTeam;
  if (!isTeam(last)) throw new Error("scoring: lastTrickWinnerTeam must be 0 or 1");
  const decl = input.declarationPointsByTeam;
  if (!Array.isArray(decl) || decl.length !== 2) {
    throw new Error("scoring: declarationPointsByTeam must be [d0,d1]");
  }

  const trickTotal = /** @type {[number, number]} */ ([
    tcp[0] + (last === 0 ? 10 : 0),
    tcp[1] + (last === 1 ? 10 : 0),
  ]);

  const target = Number(input.contractTargetPoints);
  if (!Number.isFinite(target) || target < 0) {
    throw new Error("scoring: contractTargetPoints invalid");
  }

  const kapot = Boolean(input.kapotBid);
  const ntDouble = input.noTrumpContract ? 2 : 1;
  const playingTrickPlusDecl = trickTotal[ct] + decl[ct];

  let success = false;
  if (kapot) {
    success = tricks[ct] === 8;
  } else {
    success = playingTrickPlusDecl >= target;
  }

  if (success) {
    const sb0 = Number(input.shulerBonusByTeam?.[0]) || 0;
    const sb1 = Number(input.shulerBonusByTeam?.[1]) || 0;
    return {
      delta: [
        (trickTotal[0] + decl[0]) * mult * ntDouble + sb0,
        (trickTotal[1] + decl[1]) * mult * ntDouble + sb1,
      ],
      success: true,
      bait: false,
      contractTeamTrickPlusDecl: playingTrickPlusDecl,
    };
  }

  const loserDecl =
    input.losingContractTeamDeclarationPointsForBait != null
      ? Number(input.losingContractTeamDeclarationPointsForBait)
      : decl[ct];
  if (!Number.isFinite(loserDecl) || loserDecl < 0) {
    throw new Error("scoring: losingContractTeamDeclarationPointsForBait invalid");
  }

  const bidLine = target * mult;
  const oppRaw = trickTotal[ot] + decl[ot];
  /** @type {[number, number]} */
  const delta = [0, 0];
  delta[ct] = 0;
  delta[ot] = (bidLine + oppRaw + loserDecl) * ntDouble;
  const sb0 = Number(input.shulerBonusByTeam?.[0]) || 0;
  const sb1 = Number(input.shulerBonusByTeam?.[1]) || 0;
  delta[0] += sb0;
  delta[1] += sb1;

  return {
    delta,
    success: false,
    bait: true,
    contractTeamTrickPlusDecl: playingTrickPlusDecl,
  };
}

/**
 * Full-deck card-point total (no +10): 152 with one trump, 248 all-trump, 120 no-trump (see {@link ./tricks}).
 *
 * @param {number|null|'all_trump'} contractTrump
 * @returns {number}
 */
function expectedCardPointsTotalNoLastBonus(contractTrump) {
  if (contractTrump === "all_trump") return 248;
  return contractTrump == null ? 120 : 152;
}

module.exports = {
  KAPOT_CONTRACT_POINTS,
  scoreRound,
  expectedCardPointsTotalNoLastBonus,
};
